/**
 * Turning a template into somebody's map.
 *
 * A copy with fresh ids, which is the same minting `pasteSubtree` does for a clipping and for the
 * same reason: two documents that share a node id are two documents that cannot both be synced,
 * exported or merged. A template is opened dozens of times; its ids belong to it, not to the maps
 * made from it.
 *
 * What is replaced and what survives is the whole of the design here:
 *
 * - **Replaced** — every id, the owner, the workspace, the clock and the revisions. These are
 *   facts about *this* map, and inheriting a template's would give a brand-new map a "last edited"
 *   date from whenever the bundle was built and an owner who does not exist.
 * - **Kept** — the layout, the theme, the styles, the shapes and the structure. That is what a
 *   template *is*. A copy that dropped them would be an outline, and the create flow already has
 *   a blank map for people who want one.
 *
 * The ids come from the caller rather than from a generator here, because the engine already owns
 * one and a second source of ids is a second thing to make deterministic in a test.
 */
import { DEFAULT_MAP_SETTINGS, contentOf, withContent, } from '@orbit-mind/domain';
export function instantiate(template, input) {
    const source = template.document;
    // Minted in the document's own key order so the same template and the same id source produce
    // the same map twice — which is what makes a test about this readable.
    const nodeIds = new Map();
    for (const id of Object.keys(source.nodes))
        nodeIds.set(id, input.nodeId());
    const idFor = (id) => {
        const minted = nodeIds.get(id);
        // A template referring to a node it does not contain is a broken template, and saying which
        // one beats handing the editor a document with a dangling parent.
        if (minted === undefined) {
            throw new Error(`template ${template.id} refers to a node it does not contain: ${id}`);
        }
        return minted;
    };
    const nodes = {};
    for (const [oldId, node] of Object.entries(source.nodes)) {
        const id = idFor(oldId);
        // A chart that reads a table of the template reads the same table in the copy.
        const content = contentOf(node);
        const relinked = content?.kind === 'chart' && content.source !== undefined
            ? withContent(node.metadata, {
                ...content,
                source: { ...content.source, nodeId: idFor(content.source.nodeId) },
            })
            : undefined;
        const metadata = relinked === undefined ? {} : { metadata: relinked };
        nodes[id] = {
            ...node,
            ...metadata,
            id,
            mapId: input.mapId,
            parentId: node.parentId === null ? null : idFor(node.parentId),
            createdAt: input.now,
            createdBy: input.ownerId,
            updatedAt: input.now,
            updatedBy: input.ownerId,
        };
    }
    const edges = {};
    for (const edge of Object.values(source.edges)) {
        const id = input.edgeId();
        edges[id] = {
            ...edge,
            id,
            mapId: input.mapId,
            sourceNodeId: idFor(edge.sourceNodeId),
            targetNodeId: idFor(edge.targetNodeId),
            createdAt: input.now,
            updatedAt: input.now,
        };
    }
    return {
        map: {
            ...source.map,
            id: input.mapId,
            workspaceId: input.workspaceId,
            projectId: null,
            folderId: null,
            ownerId: input.ownerId,
            title: input.title ?? template.name,
            rootNodeId: idFor(source.map.rootNodeId),
            // Not the template's viewport or settings-of-the-moment: a new map starts where a new map
            // starts. The layout and the theme are the template's contribution and they stay.
            settings: DEFAULT_MAP_SETTINGS,
            // Zero on both, and equal: a map that has never been edited has nothing to send, and a
            // `localRevision` inherited from a template would make the save badge claim otherwise.
            localRevision: 0,
            serverRevision: 0,
            createdAt: input.now,
            createdBy: input.ownerId,
            updatedAt: input.now,
            updatedBy: input.ownerId,
            deletedAt: null,
        },
        nodes,
        edges,
    };
}
//# sourceMappingURL=instantiate.js.map